import { useCallback, useEffect, useRef, useState } from 'react'
import cerrar from '@assets/nuevos-iconos/x.png'
import descargar from '@assets/nuevos-iconos/DescargarB.png'

type VisualNombrada = { nombre: string; imageUrl: string; idHqrenders?: number | null }

type PostProcessingModalProps = {
  open: boolean
  onClose: () => void
  images: VisualNombrada[]
  initialIndex: number
}

type FilterValues = {
  brightness: number
  contrast: number
  saturation: number
  vibrancy: number
  exposure: number
  sharpness: number
  temperature: number
}

const DEFAULT_FILTERS: FilterValues = {
  brightness: 0,
  contrast: 0,
  saturation: 0,
  vibrancy: 0,
  exposure: 0,
  sharpness: 0,
  temperature: 0,
}

export function PostProcessingModal({
  open,
  onClose,
  images,
  initialIndex,
}: PostProcessingModalProps) {
  const [currentIndex, setCurrentIndex] = useState(initialIndex)
  const [filters, setFilters] = useState<FilterValues>(DEFAULT_FILTERS)
  const [processedImageUrl, setProcessedImageUrl] = useState<string>('')
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const imageRef = useRef<HTMLImageElement | null>(null)
  const originalImageRef = useRef<HTMLImageElement | null>(null)
  const loadingImageRef = useRef<HTMLImageElement | null>(null)

  useEffect(() => {
    if (open) {
      setFilters(DEFAULT_FILTERS)
      setCurrentIndex(initialIndex)
      setError(null)
      setProcessedImageUrl('')
    }
  }, [open, initialIndex])

  const processImage = (img: HTMLImageElement) => {
    const canvas = canvasRef.current
    if (!canvas) return

    const ctx = canvas.getContext('2d', { willReadFrequently: true })
    if (!ctx) return

    canvas.width = img.width
    canvas.height = img.height

    ctx.drawImage(img, 0, 0)

    let imageData: ImageData
    try {
      imageData = ctx.getImageData(0, 0, canvas.width, canvas.height)
    } catch (err) {
      if (
        err instanceof DOMException ||
        (err instanceof Error && (err.name === 'SecurityError' || err.message.includes('tainted')))
      ) {
        const securityErr = new DOMException(
          'Canvas has been tainted by cross-origin data',
          'SecurityError',
        )
        throw securityErr
      }
      throw err
    }
    const data = imageData.data

    const exposureFactor = Math.pow(2, filters.exposure)

    const brightnessOffset = (filters.brightness / 100) * 255

    const contrastFactor = (100 + filters.contrast) / 100
    const contrastIntercept = 128 * (1 - contrastFactor)

    const temp = filters.temperature / 100 // -1 to 1
    const tempR = temp > 0 ? temp * 0.5 : 0 // Warm: increase red
    const tempB = temp < 0 ? Math.abs(temp) * 0.5 : 0 // Cool: increase blue

    const saturationFactor = 1 + filters.saturation / 100

    const vibrancyFactor = 1 + filters.vibrancy / 100

    // Process each pixel
    for (let i = 0; i < data.length; i += 4) {
      let r = data[i]
      let g = data[i + 1]
      let b = data[i + 2]

      // Apply exposure
      r = r * exposureFactor
      g = g * exposureFactor
      b = b * exposureFactor

      // Apply brightness (additive)
      r = r + brightnessOffset
      g = g + brightnessOffset
      b = b + brightnessOffset

      // Apply contrast
      r = r * contrastFactor + contrastIntercept
      g = g * contrastFactor + contrastIntercept
      b = b * contrastFactor + contrastIntercept

      // Apply temperature (color balance)
      if (tempR > 0) {
        // Warm: increase red, decrease blue
        r = r + tempR * 255
        b = b - tempR * 255
      } else if (tempB > 0) {
        // Cool: increase blue, decrease red
        b = b + tempB * 255
        r = r - tempB * 255
      }

      const gray = 0.299 * r + 0.587 * g + 0.114 * b
      r = gray + (r - gray) * saturationFactor
      g = gray + (g - gray) * saturationFactor
      b = gray + (b - gray) * saturationFactor

      const isSkinTone = r > 95 && r < 200 && g > 40 && g < 180 && b > 20 && b < 150
      const vibrancyMultiplier = isSkinTone ? 1 + (vibrancyFactor - 1) * 0.5 : vibrancyFactor
      const newGray = 0.299 * r + 0.587 * g + 0.114 * b
      r = newGray + (r - newGray) * vibrancyMultiplier
      g = newGray + (g - newGray) * vibrancyMultiplier
      b = newGray + (b - newGray) * vibrancyMultiplier

      data[i] = Math.max(0, Math.min(255, r))
      data[i + 1] = Math.max(0, Math.min(255, g))
      data[i + 2] = Math.max(0, Math.min(255, b))
    }

    if (filters.sharpness !== 0) {
      const sharpnessAmount = Math.abs(filters.sharpness) / 150
      const isBlur = filters.sharpness < 0

      if (isBlur) {
        const blurredData = applyGaussianBlur(imageData, sharpnessAmount * 3)
        ctx.putImageData(blurredData, 0, 0)
      } else {
        const sharpenedData = applyEnhancedSharpening(imageData, sharpnessAmount)
        ctx.putImageData(sharpenedData, 0, 0)
      }
    } else {
      ctx.putImageData(imageData, 0, 0)
    }

    setProcessedImageUrl(canvas.toDataURL('image/png'))
  }

  const loadImage = useCallback(
    (imageUrl: string) => {
      if (loadingImageRef.current) {
        loadingImageRef.current.onload = null
        loadingImageRef.current.onerror = null
        loadingImageRef.current.src = ''
      }

      setIsLoading(true)
      setError(null)
      setProcessedImageUrl('')

      const loadImageAsBlob = async () => {
        try {
          const params = {
            method: 'GET',
            mode: 'cors',
            headers: {
              'Accept-Language': 'ES',
              'Content-Type': 'application/json',
              Authorization: 'Ninpo ' + sessionStorage.getItem('access_token'),
            },
          }

          const response = await fetch(imageUrl, params as RequestInit)

          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`)
          }

          console.groupEnd()

          const blob = await response.blob()
          const blobUrl = URL.createObjectURL(blob)

          const blobImg = new Image()
          blobImg.crossOrigin = 'anonymous'
          blobImg.onload = () => {
            if (loadingImageRef.current === blobImg) {
              originalImageRef.current = blobImg
              imageRef.current = blobImg
              setIsLoading(false)
              setError(null)
              try {
                processImage(blobImg)
                URL.revokeObjectURL(blobUrl)
              } catch (err) {
                URL.revokeObjectURL(blobUrl)
                setIsLoading(false)
                setError('Error al procesar la imagen')
                console.error('Error processing image:', err)
                // Show original image without processing
                setProcessedImageUrl(imageUrl)
              }
            } else {
              URL.revokeObjectURL(blobUrl)
            }
          }
          blobImg.onerror = () => {
            URL.revokeObjectURL(blobUrl)
            setIsLoading(false)
            setError('Error al cargar la imagen desde blob')
          }
          blobImg.src = blobUrl
          loadingImageRef.current = blobImg
        } catch (fetchErr) {
          const corsInfo = {
            url: imageUrl,
            error: fetchErr instanceof Error ? fetchErr.message : String(fetchErr),
            timestamp: new Date().toISOString(),
            userAgent: navigator.userAgent,
          }

          console.group('🔴 ERROR CORS - Imagen no procesable')
          console.error('URL de la imagen:', imageUrl)
          console.error('Error:', fetchErr)
          console.error('Información completa:', corsInfo)
          console.groupEnd()

          // Try to get more info by checking headers (if possible)
          try {
            const testResponse = await fetch(imageUrl, {
              method: 'HEAD',
              mode: 'no-cors', // This won't fail but we can't read headers
            })
            console.log('HEAD request result:', testResponse)
          } catch (e) {
            // Ignore - just trying to get info
          }

          const fallbackImg = new Image()
          fallbackImg.onload = () => {
            if (loadingImageRef.current === fallbackImg) {
              setIsLoading(false)
              const errorMsg =
                '⚠️ CORS Error: No se puede procesar esta imagen.\n\n' + `URL: ${imageUrl}\n\n`
              setError(errorMsg)
              // Show original image without processing
              setProcessedImageUrl(imageUrl)
            }
          }
          fallbackImg.onerror = () => {
            setIsLoading(false)
            setError('No se pudo cargar la imagen. Verifica que la URL sea válida y accesible.')
          }
          fallbackImg.src = imageUrl
          loadingImageRef.current = fallbackImg
        }
      }

      // Start loading
      loadImageAsBlob()
    },
    [filters],
  )

  useEffect(() => {
    if (!open || images.length === 0) return

    const currentImage = images[currentIndex]
    if (!currentImage || !currentImage.imageUrl) {
      setError('URL de imagen no válida')
      return
    }

    const cleanup = loadImage(currentImage.imageUrl)

    return cleanup
  }, [open, currentIndex, images, filters, loadImage])

  const applyGaussianBlur = (imageData: ImageData, radius: number): ImageData => {
    const width = imageData.width
    const height = imageData.height
    const data = imageData.data
    const output = new ImageData(width, height)
    const outputData = output.data

    const kernelSize = Math.ceil(radius * 2) * 2 + 1
    const kernel: number[] = []
    const sigma = radius / 3
    let sum = 0

    // Generate Gaussian kernel
    for (let i = 0; i < kernelSize; i++) {
      const x = i - Math.floor(kernelSize / 2)
      const value = Math.exp(-(x * x) / (2 * sigma * sigma))
      kernel[i] = value
      sum += value
    }

    // Normalize kernel
    for (let i = 0; i < kernelSize; i++) {
      kernel[i] /= sum
    }

    // Apply horizontal blur
    const temp = new Uint8ClampedArray(data.length)
    for (let y = 0; y < height; y++) {
      for (let x = 0; x < width; x++) {
        let r = 0,
          g = 0,
          b = 0,
          a = 0

        for (let k = 0; k < kernelSize; k++) {
          const px = Math.max(0, Math.min(width - 1, x + k - Math.floor(kernelSize / 2)))
          const idx = (y * width + px) * 4
          r += data[idx] * kernel[k]
          g += data[idx + 1] * kernel[k]
          b += data[idx + 2] * kernel[k]
          a += data[idx + 3] * kernel[k]
        }

        const idx = (y * width + x) * 4
        temp[idx] = r
        temp[idx + 1] = g
        temp[idx + 2] = b
        temp[idx + 3] = a
      }
    }

    for (let y = 0; y < height; y++) {
      for (let x = 0; x < width; x++) {
        let r = 0,
          g = 0,
          b = 0,
          a = 0

        for (let k = 0; k < kernelSize; k++) {
          const py = Math.max(0, Math.min(height - 1, y + k - Math.floor(kernelSize / 2)))
          const idx = (py * width + x) * 4
          r += temp[idx] * kernel[k]
          g += temp[idx + 1] * kernel[k]
          b += temp[idx + 2] * kernel[k]
          a += temp[idx + 3] * kernel[k]
        }

        const idx = (y * width + x) * 4
        outputData[idx] = r
        outputData[idx + 1] = g
        outputData[idx + 2] = b
        outputData[idx + 3] = a
      }
    }

    return output
  }

  const applyEnhancedSharpening = (imageData: ImageData, amount: number): ImageData => {
    const width = imageData.width
    const height = imageData.height
    const data = imageData.data
    const output = new ImageData(width, height)
    const outputData = output.data

    const kernel = [0, -1, 0, -1, 5, -1, 0, -1, 0]

    for (let y = 1; y < height - 1; y++) {
      for (let x = 1; x < width - 1; x++) {
        let r = 0,
          g = 0,
          b = 0

        for (let ky = -1; ky <= 1; ky++) {
          for (let kx = -1; kx <= 1; kx++) {
            const px = x + kx
            const py = y + ky
            const idx = (py * width + px) * 4
            const kernelIdx = (ky + 1) * 3 + (kx + 1)

            r += data[idx] * kernel[kernelIdx]
            g += data[idx + 1] * kernel[kernelIdx]
            b += data[idx + 2] * kernel[kernelIdx]
          }
        }

        const idx = (y * width + x) * 4
        const originalR = data[idx]
        const originalG = data[idx + 1]
        const originalB = data[idx + 2]

        const enhancedAmount = amount * 2
        outputData[idx] = Math.max(0, Math.min(255, originalR + (r - originalR) * enhancedAmount))
        outputData[idx + 1] = Math.max(
          0,
          Math.min(255, originalG + (g - originalG) * enhancedAmount),
        )
        outputData[idx + 2] = Math.max(
          0,
          Math.min(255, originalB + (b - originalB) * enhancedAmount),
        )
        outputData[idx + 3] = data[idx + 3]
      }
    }

    // Handle edges (copy original)
    for (let y = 0; y < height; y++) {
      for (let x = 0; x < width; x++) {
        if (y === 0 || y === height - 1 || x === 0 || x === width - 1) {
          const idx = (y * width + x) * 4
          outputData[idx] = data[idx]
          outputData[idx + 1] = data[idx + 1]
          outputData[idx + 2] = data[idx + 2]
          outputData[idx + 3] = data[idx + 3]
        }
      }
    }

    return output
  }

  const handleFilterChange = (filterName: keyof FilterValues, value: number) => {
    setFilters((prev) => ({
      ...prev,
      [filterName]: value,
    }))
  }

  const handleReset = () => {
    setFilters(DEFAULT_FILTERS)
  }

  const handlePreviousImage = useCallback(() => {
    setCurrentIndex((prev) => (prev > 0 ? prev - 1 : images.length - 1))
    setFilters(DEFAULT_FILTERS)
  }, [images.length])

  const handleNextImage = useCallback(() => {
    setCurrentIndex((prev) => (prev + 1) % images.length)
    setFilters(DEFAULT_FILTERS)
  }, [images.length])

  const handleDownload = () => {
    if (!processedImageUrl) return

    const link = document.createElement('a')
    link.href = processedImageUrl
    const currentImage = images[currentIndex]
    const date = new Date()
    const formattedDate = date.toLocaleDateString('sv-SE')
    link.download = `PostProcesado-${currentImage.nombre}-${formattedDate}.png`
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  useEffect(() => {
    const handleKeyDown = (e: globalThis.KeyboardEvent) => {
      if (!open) return
      if (e.key === 'Escape') {
        onClose()
      } else if (e.key === 'ArrowLeft') {
        handlePreviousImage()
      } else if (e.key === 'ArrowRight') {
        handleNextImage()
      }
    }

    if (open) {
      window.addEventListener('keydown', handleKeyDown)
    }

    return () => {
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [open, onClose, handlePreviousImage, handleNextImage])

  if (!open || images.length === 0) return null

  const currentImage = images[currentIndex]

  return (
    <>
      <style>{`
        .modern-slider {
          -webkit-appearance: none;
          appearance: none;
          width: 100%;
          height: 10px;
          border-radius: 9999px;
          background: linear-gradient(to right, 
            rgba(170, 170, 170, 0.15) 0%, 
            rgba(99, 134, 161, 0.2) 50%, 
            rgba(170, 170, 170, 0.15) 100%);
          outline: none;
          transition: all 0.2s ease;
          cursor: pointer;
          border: 1px solid rgba(170, 170, 170, 0.1);
        }

        .modern-slider:hover {
          height: 12px;
          background: linear-gradient(to right, 
            rgba(170, 170, 170, 0.2) 0%, 
            rgba(99, 134, 161, 0.3) 50%, 
            rgba(170, 170, 170, 0.2) 100%);
          border-color: rgba(99, 134, 161, 0.3);
        }

        .modern-slider::-webkit-slider-thumb {
          -webkit-appearance: none;
          appearance: none;
          width: 22px;
          height: 22px;
          border-radius: 50%;
          background: linear-gradient(135deg, #6386A1 0%, #3C476E 100%);
          cursor: pointer;
          box-shadow: 0 2px 6px rgba(0, 0, 0, 0.3), 0 0 0 2px rgba(255, 255, 255, 0.1);
          transition: all 0.2s ease;
        }

        .modern-slider::-webkit-slider-thumb:hover {
          transform: scale(1.15);
          box-shadow: 0 3px 8px rgba(0, 0, 0, 0.4), 0 0 0 3px rgba(99, 134, 161, 0.3);
        }

        .modern-slider::-webkit-slider-thumb:active {
          transform: scale(1.1);
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.3), 0 0 0 2px rgba(99, 134, 161, 0.5);
        }

        .modern-slider::-moz-range-thumb {
          width: 22px;
          height: 22px;
          border-radius: 50%;
          background: linear-gradient(135deg, #6386A1 0%, #3C476E 100%);
          cursor: pointer;
          border: none;
          box-shadow: 0 2px 6px rgba(0, 0, 0, 0.3), 0 0 0 2px rgba(255, 255, 255, 0.1);
          transition: all 0.2s ease;
        }

        .modern-slider::-moz-range-thumb:hover {
          transform: scale(1.15);
          box-shadow: 0 3px 8px rgba(0, 0, 0, 0.4), 0 0 0 3px rgba(99, 134, 161, 0.3);
        }

        .modern-slider::-moz-range-thumb:active {
          transform: scale(1.1);
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.3), 0 0 0 2px rgba(99, 134, 161, 0.5);
        }

        .modern-slider::-moz-range-track {
          height: 10px;
          border-radius: 9999px;
          background: linear-gradient(to right, 
            rgba(170, 170, 170, 0.15) 0%, 
            rgba(99, 134, 161, 0.2) 50%, 
            rgba(170, 170, 170, 0.15) 100%);
          border: 1px solid rgba(170, 170, 170, 0.1);
        }

        .modern-slider:focus {
          outline: none;
        }

        .modern-slider:focus::-webkit-slider-thumb {
          box-shadow: 0 3px 8px rgba(0, 0, 0, 0.4), 0 0 0 3px rgba(99, 134, 161, 0.4);
        }

        .modern-slider:focus::-moz-range-thumb {
          box-shadow: 0 3px 8px rgba(0, 0, 0, 0.4), 0 0 0 3px rgba(99, 134, 161, 0.4);
        }
      `}</style>
      <div
        className='fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-80'
        onClick={onClose}
      >
        <div
          className='flex h-[90vh] w-[95vw] max-w-7xl overflow-hidden rounded-lg bg-C/Color1 shadow-2xl'
          onClick={(e) => e.stopPropagation()}
        >
          <div className='relative flex flex-1 items-center justify-center bg-gradient-to-br from-gray-900 to-black'>
            {images.length > 1 && (
              <>
                <button
                  onClick={handlePreviousImage}
                  className='absolute left-4 z-10 flex h-12 w-12 items-center justify-center text-3xl text-white/80 transition-all hover:scale-110 hover:text-white focus:outline-none'
                  aria-label='Imagen anterior'
                >
                  <svg
                    width='24'
                    height='24'
                    viewBox='0 0 24 24'
                    fill='none'
                    stroke='currentColor'
                    strokeWidth='2.5'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    className='drop-shadow-lg'
                  >
                    <path d='M15 18l-6-6 6-6' />
                  </svg>
                </button>
                <button
                  onClick={handleNextImage}
                  className='absolute right-4 z-10 flex h-12 w-12 items-center justify-center text-3xl text-white/80 transition-all hover:scale-110 hover:text-white focus:outline-none'
                  aria-label='Imagen siguiente'
                >
                  <svg
                    width='24'
                    height='24'
                    viewBox='0 0 24 24'
                    fill='none'
                    stroke='currentColor'
                    strokeWidth='2.5'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    className='drop-shadow-lg'
                  >
                    <path d='M9 18l6-6-6-6' />
                  </svg>
                </button>
              </>
            )}

            {error ? (
              <div className='flex flex-col items-center justify-center gap-4 text-white'>
                <div className='whitespace-pre-line text-center text-lg font-semibold text-red-400'>
                  {error}
                </div>
                <button
                  onClick={() => {
                    const currentImage = images[currentIndex]
                    if (currentImage?.imageUrl) {
                      loadImage(currentImage.imageUrl)
                    }
                  }}
                  className='hover:bg-C/Color3/90 rounded-lg bg-C/Color3 px-4 py-2 text-C/Color1 transition-colors'
                >
                  Reintentar
                </button>
              </div>
            ) : processedImageUrl ? (
              <img
                src={processedImageUrl}
                alt={currentImage.nombre}
                className='max-h-full max-w-full object-contain'
              />
            ) : (
              <div className='flex flex-col items-center justify-center gap-2 text-white'>
                <div className='h-8 w-8 animate-spin rounded-full border-4 border-white border-t-transparent'></div>
                <div>Cargando imagen...</div>
              </div>
            )}

            {/* Image counter */}
            {images.length > 1 && (
              <div className='absolute bottom-4 left-1/2 -translate-x-1/2 rounded-full bg-black/70 px-4 py-2 text-sm text-white backdrop-blur-sm'>
                {currentIndex + 1} / {images.length}
              </div>
            )}
          </div>

          {/* Right Panel - Controls */}
          <div className='w-96 overflow-y-auto border-l border-C/Color4 bg-gradient-to-b from-C/Color10 to-C/Color1 p-6 shadow-xl'>
            <div className='mb-6 flex items-center justify-between border-b border-C/Color4 pb-4'>
              <h2 className='text-2xl font-bold text-C/Color5'>Post-procesado</h2>
              <button
                onClick={onClose}
                className='rounded-full p-1 text-C/Color5 transition-colors hover:bg-C/Color4 hover:text-C/Color3'
              >
                <img
                  src={cerrar}
                  alt='Cerrar'
                  className='h-5 w-5'
                />
              </button>
            </div>

            <button
              onClick={handleReset}
              className='mb-6 flex w-full items-center justify-center rounded-md border-2 border-C/Color3 bg-C/Color1 px-3 py-[9px] text-sm font-medium text-C/Color3 transition-colors hover:bg-C/Color3 hover:text-C/Color1'
            >
              Reset
            </button>

            <div className='space-y-7'>
              <div>
                <div className='mb-3'>
                  <label className='text-sm font-medium text-C/Color5'>Brillo</label>
                </div>
                <div className='relative'>
                  <input
                    type='range'
                    min='-100'
                    max='100'
                    value={filters.brightness}
                    onChange={(e) => handleFilterChange('brightness', Number(e.target.value))}
                    className='modern-slider'
                    style={{
                      background: `linear-gradient(to right, 
                       rgba(0, 0, 0, 0.3) 0%, 
                       rgba(128, 128, 128, 0.5) ${50 + filters.brightness / 2}%, 
                       rgba(255, 255, 255, 0.3) 100%)`,
                    }}
                  />
                </div>
              </div>

              <div>
                <div className='mb-3'>
                  <label className='text-sm font-medium text-C/Color5'>Contraste</label>
                </div>
                <div className='relative'>
                  <input
                    type='range'
                    min='-100'
                    max='100'
                    value={filters.contrast}
                    onChange={(e) => handleFilterChange('contrast', Number(e.target.value))}
                    className='modern-slider'
                  />
                </div>
              </div>

              <div>
                <div className='mb-3'>
                  <label className='text-sm font-medium text-C/Color5'>Saturación</label>
                </div>
                <div className='relative'>
                  <input
                    type='range'
                    min='-100'
                    max='100'
                    value={filters.saturation}
                    onChange={(e) => handleFilterChange('saturation', Number(e.target.value))}
                    className='modern-slider'
                  />
                </div>
              </div>

              <div>
                <div className='mb-3'>
                  <label className='text-sm font-medium text-C/Color5'>Vibrancia</label>
                </div>
                <div className='relative'>
                  <input
                    type='range'
                    min='-100'
                    max='100'
                    value={filters.vibrancy}
                    onChange={(e) => handleFilterChange('vibrancy', Number(e.target.value))}
                    className='modern-slider'
                  />
                </div>
                <p className='text-C/Color5/50 mt-2 text-xs italic'>Saturación selectiva</p>
              </div>

              <div>
                <div className='mb-3'>
                  <label className='text-sm font-medium text-C/Color5'>Exposición</label>
                </div>
                <div className='relative'>
                  <input
                    type='range'
                    min='-200'
                    max='200'
                    step='1'
                    value={Math.round(filters.exposure * 100)}
                    onChange={(e) => handleFilterChange('exposure', Number(e.target.value) / 100)}
                    className='modern-slider'
                  />
                </div>
              </div>

              <div>
                <div className='mb-3'>
                  <label className='text-sm font-medium text-C/Color5'>Temperatura</label>
                </div>
                <div className='relative'>
                  <input
                    type='range'
                    min='-100'
                    max='100'
                    value={filters.temperature}
                    onChange={(e) => handleFilterChange('temperature', Number(e.target.value))}
                    className='modern-slider'
                    style={{
                      background: `linear-gradient(to right, 
                       rgba(0, 150, 255, 0.4) 0%, 
                       rgba(255, 255, 255, 0.3) 50%, 
                       rgba(255, 150, 0, 0.4) 100%)`,
                    }}
                  />
                </div>
                <div className='text-C/Color5/50 mt-2 flex justify-between text-xs'>
                  <span>Fría</span>
                  <span>Cálida</span>
                </div>
              </div>

              <div>
                <div className='mb-3'>
                  <label className='text-sm font-medium text-C/Color5'>Nitidez</label>
                </div>
                <div className='relative'>
                  <input
                    type='range'
                    min='-150'
                    max='150'
                    value={filters.sharpness}
                    onChange={(e) => handleFilterChange('sharpness', Number(e.target.value))}
                    className='modern-slider'
                  />
                </div>
                <div className='text-C/Color5/50 mt-2 flex justify-between text-xs'>
                  <span>Desenfoque</span>
                  <span>Enfocar</span>
                </div>
              </div>
            </div>

            <button
              onClick={handleDownload}
              className='hover:bg-C/Color3/90 mt-8 flex h-12 w-full items-center justify-center gap-2 rounded-lg border-2 border-C/Color7 bg-C/Color3 font-semibold text-C/Color1 shadow-lg transition-all active:scale-95'
            >
              <img
                src={descargar}
                alt='Descargar'
                className='h-7'
              />
              Exportar
            </button>
          </div>
        </div>

        <canvas
          ref={canvasRef}
          className='hidden'
        />
      </div>
    </>
  )
}
